import React, { useEffect } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import { cloneDeep, find, get, includes } from 'lodash';
import FormField from '..';

import Button from '../../../../../../../common/components/Button/Button';
import { getScheduleMainField, getScheduleMainFieldByType } from '../../../../../../../common/services/ValidationRules';
import { PGE_SCHEDULE_TYPE } from '../../../../constants';
import { getMultiplySourceValue, handleFieldChanges } from '../../helper';
import { ScheduleControlArea, ScheduleError, ScheduleFieldWrap, ScheduleItemRemoveWrap, ScheduleItemWrap } from './style';

const getError = (formErrors, scheduleName) => {
  let error;

  Object.keys(formErrors).forEach((fieldName) => {
    const parts = fieldName.split('#');

    if (parts[1] === scheduleName) error = formErrors[fieldName];
  });

  return error;
};

export const SCHEDULE_MAX_SECONDS_COUNT = 86399;

function ScheduleField(props) {
  const { field, onChange, formState, formErrors, node, ...rest } = props;
  const { fields, name: scheduleName } = field;
  const schedules = get(formState, 'staticSection.schedules', []);
  const schedule = cloneDeep(schedules.find((item) => item.dayType === scheduleName));
  const schedulesWithoutTarget = cloneDeep(schedules.filter((item) => item.dayType !== scheduleName));
  const error = getError(formErrors, scheduleName);
  const scheduleMainField = getScheduleMainField(formState.staticSection);
  const scheduleFieldName = schedule && schedule.items.length > 0 ? getScheduleMainFieldByType(schedule.items[0].type) : null;
  const isRootNode = schedule && schedule.items[0].type === PGE_SCHEDULE_TYPE;
  const mainValue = scheduleMainField && !isRootNode ? formState.staticSection[scheduleMainField] : null;

  const getPowerValue = () =>
    scheduleFieldName.toLowerCase().includes('power') && get(node, 'staticSection.maxPower') !== mainValue ? mainValue * 1000 : mainValue;

  const getUpdatedScheduleItems = (scheduleItems) =>
    scheduleItems.length !== 1
      ? scheduleItems
      : scheduleItems.map((item) => {
          const newScheduleItem = {
            ...item,
            [scheduleFieldName]: getPowerValue(),
          };

          const powerSCs = document.querySelectorAll('.maxPowerSC:disabled');
          const loadSCs = document.querySelectorAll('.maxLoadSC:disabled');
          const multipliedValue = getMultiplySourceValue({
            node: newScheduleItem,
            field: find(fields, (field) => field.name === scheduleFieldName),
          });

          powerSCs.forEach((powerSC) => {
            powerSC.value = multipliedValue;
          });

          loadSCs.forEach((loadSC) => {
            loadSC.value = multipliedValue;
          });

          return newScheduleItem;
        });

  useEffect(() => {
    if (rest.scSchedule && !isRootNode && mainValue !== null && mainValue !== '' && schedule.items.length > 0 && scheduleFieldName) {
      onChange({
        target: {
          name: 'schedules',
          value: schedules.map((schedule) => ({
            ...schedule,
            items: getUpdatedScheduleItems(schedule.items),
          })),
        },
      });
    }
  }, [mainValue]);

  const fieldChange = (event, position) => {
    const { name, value } = handleFieldChanges(event);
    const items = cloneDeep(schedule.items);

    items[position][name] = (rest.scSchedule && !value.toString().length) || !name.toLowerCase().includes('power') ? value : value * 1000;

    items[0].from = 0;

    onChange({
      target: {
        name: 'schedules',
        value: [...schedulesWithoutTarget, { dayType: scheduleName, items }],
      },
    });
  };

  const addRange = () => {
    const newRange = cloneDeep(schedule.items[schedule.items.length - 1]);

    if (newRange.to < SCHEDULE_MAX_SECONDS_COUNT) {
      newRange.from = newRange.to;
      newRange.to = SCHEDULE_MAX_SECONDS_COUNT;
    }

    onChange({
      target: {
        name: 'schedules',
        value: [
          ...schedulesWithoutTarget,
          {
            dayType: scheduleName,
            items: [...schedule.items, newRange],
          },
        ],
      },
    });
  };

  const removeScheduleItem = (key) => {
    const newScheduleItems = schedule.items.filter((_, index) => index !== key);

    if (rest.scSchedule && newScheduleItems.length === 1) {
      newScheduleItems[0] = {
        ...newScheduleItems[0],
        from: 0,
        to: SCHEDULE_MAX_SECONDS_COUNT,
      };

      if (!isRootNode) {
        newScheduleItems[0][scheduleFieldName] = getPowerValue();
      } else {
        if (get(newScheduleItems[0], scheduleFieldName, '').toString().length === 0) {
          newScheduleItems[0][scheduleFieldName] = 0;
        }

        if (get(newScheduleItems[0], 'cost', '').toString().length === 0) {
          newScheduleItems[0].cost = 0;
        }
      }
    }

    onChange({
      target: {
        name: 'schedules',
        value: [
          ...schedulesWithoutTarget,
          {
            dayType: scheduleName,
            items: newScheduleItems,
          },
        ],
      },
    });
  };

  const getEmptyError = (item, field) => {
    if (rest.scSchedule && schedule.items.length > 1 && get(item, field.name, '').toString().length === 0) {
      return 'Required';
    }

    return null;
  };

  const getScheduleInfoMessage = () => {
    if (rest.scSchedule && schedule && schedule.items.length === 1) {
      return 'Please create the schedule with at least two time slots.';
    }

    return null;
  };

  const getDisabledField = (fieldName) =>
    rest.scSchedule && schedule.items.length === 1 && scheduleFieldName && includes(['from', 'to', scheduleFieldName, 'cost'], fieldName);

  return (
    <ScheduleFieldWrap>
      {schedule &&
        schedule.items.map((item, key) => (
          <ScheduleItemWrap key={`${key}-${schedule.items.length}`}>
            {schedule.items.length > 1 && (
              <ScheduleItemRemoveWrap onClick={() => removeScheduleItem(key)}>
                <CloseIcon />
              </ScheduleItemRemoveWrap>
            )}
            {fields.map((field) => (
              <FormField
                key={field.name}
                field={{
                  ...field,
                  disabled: getDisabledField(field.name),
                }}
                formState={item}
                {...rest}
                node={item}
                onChange={(event) => fieldChange(event, key)}
                error={() => getEmptyError(item, field)}
              />
            ))}
          </ScheduleItemWrap>
        ))}
      <ScheduleControlArea>
        <ScheduleError>{error || getScheduleInfoMessage()}</ScheduleError>
        <Button onClick={addRange}> Add range </Button>
      </ScheduleControlArea>
    </ScheduleFieldWrap>
  );
}

export default ScheduleField;
